import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, TrendingUp, ArrowUpRight, ArrowDownRight, Info, Sparkles } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Top-10 Stocks panel — reads /api/stocks/top10 and renders three
 * ranked consensus lists (Overall / Mid-Cap / Small-Cap), plus
 * adds/removals since yesterday, methodology, and the required
 * "not investment advice" disclaimer.
 *
 * The three tiers share the same 12-firm methodology and 4-query
 * multi-pass research pipeline — Mid and Small-Cap variants scope
 * their Perplexity queries to the corresponding market-cap band
 * ($2B-$10B and $300M-$2B respectively).
 *
 * Subscribe toggle flips `subscribers.stocks_active` via
 * /api/manage/{token}/stocks-toggle. When on, the daily news email
 * (or a standalone stocks-only email if no news feed exists) is
 * prepended with the Top-10 block.
 */

const TIERS = [
  { key: "overall", label: "Overall", subtitle: "All caps" },
  { key: "mid_cap", label: "Mid Cap", subtitle: "$2B–$10B" },
  { key: "small_cap", label: "Small Cap", subtitle: "$300M–$2B" },
];

export default function StocksPanel({ token, initiallySubscribed }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tier, setTier] = useState("overall");
  const [subscribed, setSubscribed] = useState(!!initiallySubscribed);
  const [toggling, setToggling] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await axios.get(`${API}/stocks/top10`);
        if (!cancelled) setData(r.data);
      } catch (e) {
        if (!cancelled) toast.error("Couldn't load the Top-10 right now.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => { setSubscribed(!!initiallySubscribed); }, [initiallySubscribed]);

  const toggleSubscribe = async () => {
    if (!token) {
      toast.error("Sign in to subscribe.");
      return;
    }
    setToggling(true);
    const next = !subscribed;
    try {
      await axios.put(`${API}/manage/${token}/stocks-toggle`, { active: next });
      setSubscribed(next);
      toast.success(
        next
          ? "Subscribed. The Top-10 will land in your inbox each morning."
          : "Unsubscribed from the Top-10 email.",
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't update your subscription.");
    } finally {
      setToggling(false);
    }
  };

  // Pull the current tier's rows + diff. The API returns overall at
  // the top level (top10 / adds / removals) and Mid/Small nested under
  // `mid_cap` / `small_cap` with an additional `rising` array.
  const currentSlice = (() => {
    if (!data) return { top10: [], adds: [], removals: [], rising: [] };
    if (tier === "overall") return { top10: data.top10 || [], adds: data.adds || [], removals: data.removals || [], rising: [] };
    if (tier === "mid_cap") return { rising: [], ...(data.mid_cap || {}) };
    if (tier === "small_cap") return { rising: [], ...(data.small_cap || {}) };
    return { top10: [], adds: [], removals: [], rising: [] };
  })();
  const activeTierMeta = TIERS.find((t) => t.key === tier) || TIERS[0];
  const risingMap = new Map((currentSlice.rising || []).map((r) => [r.ticker, r]));

  return (
    <div data-testid="stocks-panel" className="space-y-6">
      {/* Header row: label + subscribe toggle */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 border-2 border-black bg-white p-5">
        <div>
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] font-bold">
            <TrendingUp className="w-4 h-4" />
            Top 10 Stocks — consensus watch
          </div>
          <div className="font-display text-2xl sm:text-3xl font-black tracking-tight mt-1">
            What the big firms are pounding the table on.
          </div>
          <div className="text-sm text-neutral-700 mt-1">
            {data?.date ? (
              <>Refreshed {new Date(data.generated_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}. Rank = number of tracked firms whose current top-picks list references the ticker.</>
            ) : (
              "Loading today's snapshot…"
            )}
          </div>
        </div>
        {token && (
          <button
            data-testid="stocks-subscribe-toggle"
            onClick={toggleSubscribe}
            disabled={toggling}
            className={`whitespace-nowrap px-5 py-3 text-[11px] uppercase tracking-[0.18em] font-bold border-2 border-black transition-colors ${
              subscribed ? "bg-black text-[#FFD600] hover:bg-neutral-800" : "bg-[#FFD600] hover:bg-yellow-400"
            }`}
          >
            {toggling ? (
              <Loader2 className="w-3 h-3 animate-spin inline" />
            ) : subscribed ? (
              "You're subscribed — turn off"
            ) : (
              "Subscribe to the Top 10"
            )}
          </button>
        )}
      </div>

      {/* Sub-tabs: Overall / Mid Cap / Small Cap */}
      <div role="tablist" aria-label="Cap tier" className="flex flex-wrap items-end gap-0 border-b-2 border-black">
        {TIERS.map((t) => {
          const isActive = tier === t.key;
          return (
            <button
              key={t.key}
              role="tab"
              aria-selected={isActive}
              data-testid={`stocks-tier-${t.key}`}
              onClick={() => setTier(t.key)}
              className={`px-5 py-3 border-2 border-black border-b-0 -mb-[2px] transition-colors text-left ${
                isActive ? "bg-black text-[#FFD600]" : "bg-white hover:bg-[#FFFBE6] text-black"
              }`}
            >
              <div className="text-[11px] uppercase tracking-[0.18em] font-bold leading-none">{t.label}</div>
              <div className={`text-[10px] mt-1 leading-none ${isActive ? "text-[#FFD600]/80" : "text-neutral-500"}`}>{t.subtitle}</div>
            </button>
          );
        })}
      </div>

      {/* Adds / removals since yesterday for the selected tier */}
      {(currentSlice.adds?.length || currentSlice.removals?.length) ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="stocks-diff-row">
          {currentSlice.adds?.length ? (
            <div className="border border-black/20 bg-[#F0FDF4] p-4">
              <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] font-bold text-green-800">
                <ArrowUpRight className="w-3 h-3" />
                New today ({activeTierMeta.label.toLowerCase()})
              </div>
              <div className="mt-1.5 font-mono text-sm font-semibold text-neutral-900">
                {currentSlice.adds.join(" · ")}
              </div>
            </div>
          ) : null}
          {currentSlice.removals?.length ? (
            <div className="border border-black/20 bg-[#FEF2F2] p-4">
              <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] font-bold text-red-800">
                <ArrowDownRight className="w-3 h-3" />
                Rolled off ({activeTierMeta.label.toLowerCase()})
              </div>
              <div className="mt-1.5 font-mono text-sm font-semibold text-neutral-900">
                {currentSlice.removals.join(" · ")}
              </div>
            </div>
          ) : null}
        </div>
      ) : null}

      {/* Rising Stars — tickers that gained a firm attribution vs
          yesterday. Mid/Small cap only (overall is dominated by
          stable mega-caps). */}
      {currentSlice.rising?.length ? (
        <div className="border-2 border-black bg-gradient-to-r from-[#FFFBE6] via-[#FFF9CC] to-[#FFFBE6] p-4" data-testid="stocks-rising-callout">
          <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] font-bold text-black">
            <Sparkles className="w-3 h-3" />
            Rising Stars ({activeTierMeta.label.toLowerCase()}) — picked up firm coverage since yesterday
          </div>
          <div className="mt-2 flex flex-wrap gap-2">
            {currentSlice.rising.map((r) => (
              <span
                key={r.ticker}
                data-testid={`stocks-rising-${r.ticker}`}
                className="inline-flex items-center gap-1 border border-black bg-white px-2 py-1 text-xs"
              >
                <span className="font-mono font-black">{r.ticker}</span>
                <span className="text-neutral-500 font-mono">
                  {r.prior_score}→{r.current_score}
                </span>
                <span className="inline-block px-1 bg-black text-[#FFD600] font-mono font-bold text-[10px]">
                  +{r.delta}
                </span>
              </span>
            ))}
          </div>
        </div>
      ) : null}

      {/* Ranked table */}
      <div className="border-2 border-black bg-white overflow-hidden" data-testid="stocks-table">
        {loading ? (
          <div className="p-8 flex items-center justify-center text-sm text-neutral-500 gap-2">
            <Loader2 className="w-4 h-4 animate-spin" />
            Loading the Top 10…
          </div>
        ) : !currentSlice.top10?.length ? (
          <div className="p-8 text-center text-sm text-neutral-600">
            No {activeTierMeta.label.toLowerCase()} consensus surfaced in today&rsquo;s snapshot. The next refresh runs at 04:00 UTC — or an admin can trigger it manually.
          </div>
        ) : (
          <table className="w-full text-left">
            <thead className="bg-[#0A0A0A] text-white text-[10px] uppercase tracking-[0.18em]">
              <tr>
                <th className="px-4 py-3 w-12">#</th>
                <th className="px-4 py-3">Ticker</th>
                <th className="px-4 py-3 hidden sm:table-cell">Company</th>
                <th className="px-4 py-3 w-20 text-center">Score</th>
                <th className="px-4 py-3">Firms</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-200">
              {currentSlice.top10.map((t, i) => {
                const risingInfo = risingMap.get(t.ticker);
                return (
                <tr key={t.ticker} data-testid={`stocks-row-${t.ticker}`} className={i < 3 ? "bg-[#FFFBE6]" : ""}>
                  <td className="px-4 py-3 text-lg font-black font-mono text-neutral-500">
                    {String(i + 1).padStart(2, "0")}
                  </td>
                  <td className="px-4 py-3 font-mono font-black text-base">
                    <div className="flex items-center gap-1.5">
                      {t.ticker}
                      {risingInfo && (
                        <span
                          title={`Gained ${risingInfo.delta} firm${risingInfo.delta === 1 ? "" : "s"} vs yesterday (${risingInfo.prior_score}→${risingInfo.current_score})`}
                          data-testid={`stocks-rising-badge-${t.ticker}`}
                          className="inline-flex items-center gap-0.5 text-[9px] px-1.5 py-0.5 bg-black text-[#FFD600] font-mono font-bold rounded-none"
                        >
                          <Sparkles className="w-2.5 h-2.5" />
                          +{risingInfo.delta}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-neutral-700 hidden sm:table-cell">
                    {t.name || "—"}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className="inline-flex items-center justify-center min-w-[28px] h-6 px-2 bg-black text-[#FFD600] font-mono font-bold text-xs">
                      {t.score}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-[11px] text-neutral-600 leading-tight">
                    {Array.from(new Set((t.firms || []).map((f) => f.firm))).join(", ")}
                  </td>
                </tr>
              );})}
            </tbody>
          </table>
        )}
      </div>

      {/* Methodology */}
      <details className="border border-black/20 bg-[#FFFBE6] p-4" data-testid="stocks-methodology">
        <summary className="cursor-pointer flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] font-bold">
          <Info className="w-3 h-3" />
          Methodology
        </summary>
        <p className="mt-3 text-sm text-neutral-800 leading-relaxed">
          {data?.methodology || "Loading…"}
        </p>
      </details>

      {/* Disclaimer */}
      <div className="border border-black/20 bg-neutral-50 p-4 text-[11px] text-neutral-600 leading-relaxed" data-testid="stocks-disclaimer">
        <span className="font-bold uppercase tracking-[0.14em] text-neutral-800">Disclaimer.</span>{" "}
        {data?.disclaimer || "Not investment advice."}
      </div>
    </div>
  );
}
